/**
 * Where an unauthenticated visitor to this app is sent.
 *
 * The shell on the apex owns sign-in, so an app should never render its own
 * login form — that would give two ways in, only one of which knows about apps.
 * The app bounces to the shell carrying `?next=`, and the shell returns the
 * visitor to exactly the page they asked for rather than the app's home.
 *
 * In local development there is no shell running, so the in-app login stays the
 * fallback and nothing about the dev loop changes.
 */
const SHELL_ORIGIN = import.meta.env['VITE_SHELL_ORIGIN'] ?? 'https://commandcenterscc.com'

/** True when this build should defer sign-in to the shell. */
export function shellOwnsAuth(): boolean {
  return !import.meta.env.DEV
}

/**
 * The shell URL to bounce to, preserving where the visitor was heading.
 *
 * `next` is sent absolute so the shell can validate it against the apps the
 * caller can actually reach — a relative path would give it nothing to check the
 * host against, and an unvalidated redirect target is an open redirect.
 */
export function shellBounceUrl(here: string): string {
  const url = new URL('/', SHELL_ORIGIN)
  url.searchParams.set('next', here)
  return url.toString()
}

/** The shell's front door, with no `next` — a clean restart of sign-in. */
export function shellHomeUrl(): string {
  return new URL('/', SHELL_ORIGIN).toString()
}

/** The shell's apps room — every app, plus the admin panels. */
export function shellAppsUrl(): string {
  return new URL('/apps', SHELL_ORIGIN).toString()
}
